import { useMutation, useQueryClient } from '@tanstack/react-query';
import { differenceInHours } from 'date-fns';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonProps } from 'src/components/Button';
import { useSession } from 'src/contexts/SessionContext';
import {
  Appointment,
  AppointmentStatus,
  WaitListEntry,
} from 'src/models/appointment/_types';
import {
  checkAppointmentEligibility,
  createAppointmentVisit,
  deleteWaitListEntry,
  updateAppointment,
} from 'src/models/appointment/appointment';
import { isSpotAvailable } from 'src/models/appointment/utils';
import { WaitListType } from 'src/models/service/_types';
import { AccountManager } from 'src/models/user/_types';
import { Visit, VisitStatus } from 'src/models/visit/_types';
import { updateVisit } from 'src/models/visit/visit';
import { Identifier } from 'src/types/utilityTypes';
import toast from 'src/utils/toast';

export type ActionTextProps = {
  bgColor?: string;
  color?: string;
  text: string;
};

export const useAppointmentDrawerActions = ({
  appointment,
  dependents = [],
}: {
  appointment: Appointment;
  dependents?: AccountManager[];
}) => {
  const { user, session } = useSession();
  const { t } = useTranslation('translation', {
    keyPrefix: 'admin.platform.appointment.drawer.appointmentDrawerActions',
  });
  const [showConfirm, setShowConfirm] = useState(false);
  const [showCancelOptions, setShowCancelOptions] = useState(false);
  const queryClient = useQueryClient();

  // Find all people in this appointment (visits and waitlist entries)
  const peopleInAppointment = useMemo(
    () =>
      [
        ...appointment.visits.map((v) => ({ id: v.userId, type: 'visit' })),
        ...appointment.entries.map((e) => ({ id: e.userId, type: 'entry' })),
      ].filter(
        (person, index, self) =>
          // Remove duplicates and only include current user or their dependents
          index === self.findIndex((p) => p.id === person.id) &&
          (person.id === user.id ||
            (dependents &&
              dependents.some((dep) => dep.dependentId === person.id)))
      ),
    [appointment.visits, appointment.entries, user.id, dependents]
  );

  // Create options for the autocomplete
  const personOptions = peopleInAppointment.map((person) => {
    const isCurrentUser = person.id === user.id;
    const dependent = dependents?.find((dep) => dep.dependentId === person.id);
    const label = isCurrentUser
      ? 'You'
      : dependent?.dependentName || `User ${person.id}`;

    return {
      id: person.id || '',
      title: label,
      userId: person.id,
    };
  });

  const [selectedUserId, setSelectedUserId] = useState<Identifier>(
    personOptions[0]?.id || user.id
  );

  const selectedPerson =
    personOptions.find((option) => option.id === selectedUserId) || null;

  // Use selectedUserId if provided (for dependents), otherwise use current user
  const visit = appointment.visits.find(
    (v) => v.userId === selectedUserId
  ) as Visit;
  const entry = appointment.entries.find(
    (e) => e.userId === selectedUserId
  ) as WaitListEntry;

  enum VisitActionStatus {
    EarlyReserved = 'early_reserved', // reserved, outside window
    EarlyConfirmed = 'early_confirmed', // confirmed, outside window
    Confirmed = 'confirmed', // able to check in
    Completed = 'completed', // checked in
    Cancelled = 'cancelled', // cancelled or late cancelled or no show
  }

  enum EntryActionStatus {
    AutoFilledWaitlist, // auto filled waitlist, no spots
    FirstComeFirstServeWaitlist, // first come first serve waitlist, no spots
    OpenWaitlist,
  }

  type StatusProps = {
    appointment: Appointment;
    visit: Visit;
    entry: WaitListEntry;
  };

  const determineStatus = ({
    appointment,
    visit,
    entry,
  }: StatusProps): VisitActionStatus | EntryActionStatus => {
    if (entry) {
      return determineEntryStatus({ appointment });
    }
    return determineVisitStatus({ appointment, visit });
  };

  const determineVisitStatus = ({ appointment, visit }): VisitActionStatus => {
    const withinTwoHours =
      differenceInHours(appointment.startTime, new Date()) < 2;

    if (
      visit.status == VisitStatus.Reserved ||
      visit.status == VisitStatus.Registered
    ) {
      if (!withinTwoHours) {
        return visit.status == VisitStatus.Reserved
          ? VisitActionStatus.EarlyReserved
          : VisitActionStatus.EarlyConfirmed;
      }

      return VisitActionStatus.Confirmed;
    } else if (visit.status == VisitStatus.Completed) {
      return VisitActionStatus.Completed;
    }

    return VisitActionStatus.Cancelled;
  };

  const determineEntryStatus = ({ appointment }): EntryActionStatus => {
    if (!isSpotAvailable(appointment)) {
      if (
        appointment.service.waitlistType != WaitListType.FirstComeFirstServe
      ) {
        return EntryActionStatus.AutoFilledWaitlist;
      }

      return EntryActionStatus.FirstComeFirstServeWaitlist;
    }
    return EntryActionStatus.OpenWaitlist;
  };

  type ButtonPropsWithLabel = ButtonProps & { label: string };

  type EntryButtonProps = {
    [key in EntryActionStatus]: ButtonPropsWithLabel[];
  };

  const waitlistButtonProps: EntryButtonProps = {
    [EntryActionStatus.AutoFilledWaitlist]: [
      {
        label: 'Remove from waitlist',
        variant: 'bordered',
        color: 'neutral',
        onClick: () => {
          setShowConfirm(true);
        },
      },
    ],
    [EntryActionStatus.FirstComeFirstServeWaitlist]: [
      {
        label: 'Book now',
        disabled: true,
        variant: 'prominent',
        color: 'primary',
      },
      {
        label: 'Remove from waitlist',
        variant: 'bordered',
        color: 'neutral',
        onClick: () => {
          setShowConfirm(true);
        },
      },
    ],
    [EntryActionStatus.OpenWaitlist]: [
      {
        label: 'Book now',
        onClick: () => {
          handlWaitlistToVisit();
        },
        variant: 'prominent',
        color: 'primary',
      },
      {
        label: 'Remove from waitlist',
        variant: 'bordered',
        color: 'neutral',
        onClick: () => {
          setShowConfirm(true);
        },
      },
    ],
  };

  type VisitButtonProps = {
    [key in VisitActionStatus]: ButtonPropsWithLabel[];
  };
  const actionButtonProps: VisitButtonProps = {
    [VisitActionStatus.EarlyReserved]: [
      {
        label: 'Confirm going',
        variant: 'prominent',
        color: 'primary',
        onClick: () => {
          registerMutation.mutate(visit);
        },
      },
    ],
    [VisitActionStatus.EarlyConfirmed]: [
      {
        label: 'Check In',
        variant: 'prominent',
        color: 'neutral',
        disabled: true,
      },
    ],
    [VisitActionStatus.Confirmed]: [
      {
        label: 'Check In',
        color: 'primary',
        variant: 'prominent',
        onClick: () => {
          checkInMutation.mutate(visit);
        },
      },
    ],
    [VisitActionStatus.Completed]: [],
    [VisitActionStatus.Cancelled]: [],
  };

  if (!appointment.service.disableCancellations) {
    Object.keys(actionButtonProps).forEach((key) => {
      if (session.disableClientCheckin || session.requireBarcodesForCheckin) {
        actionButtonProps[key] = [];
      }

      actionButtonProps[key].push({
        label: 'Cancel or change',
        color: 'neutral',
        variant: 'bordered',
        onClick: () => {
          setShowCancelOptions(true);
        },
      });
    });
  }

  type VisitActionTextProps = {
    [key in VisitActionStatus]: ActionTextProps;
  };

  const cancelOnly =
    session.disableClientCheckin || session.requireBarcodesForCheckin;

  // Create a function to generate dynamic text based on selected person
  const getActionTextProps = (
    selectedPersonName: string
  ): VisitActionTextProps => {
    const isCurrentUser = selectedPersonName === 'You';
    const possessivePronoun = isCurrentUser ? 'your' : 'their';
    const subjectPronoun = isCurrentUser ? 'You' : 'They';

    return {
      [VisitActionStatus.EarlyReserved]: {
        text: cancelOnly
          ? `Change ${possessivePronoun} session`
          : `Confirm ${
              !appointment.service.disableCancellations ? 'or change' : ''
            } ${possessivePronoun} session`,
      },
      [VisitActionStatus.EarlyConfirmed]: {
        text: cancelOnly
          ? 'Confirmed'
          : `${subjectPronoun} can check in 2 hours before the session`,
      },
      [VisitActionStatus.Confirmed]: {
        text: cancelOnly
          ? `Cancel ${possessivePronoun} session`
          : `Check in ${
              !appointment.service.disableCancellations ? 'or change' : ''
            } ${possessivePronoun} session`,
      },
      [VisitActionStatus.Completed]: {
        text: 'Checked in',
        color: 'success.darkest',
        bgColor: 'success.light',
      },
      [VisitActionStatus.Cancelled]: { text: 'Cancelled' },
    };
  };

  type EntryActionTextProps = {
    [key in EntryActionStatus]: ActionTextProps;
  };

  const waitlistActionTextProps: EntryActionTextProps = {
    [EntryActionStatus.AutoFilledWaitlist]: {
      text: 'Open spots are filled by waitlist order',
    },
    [EntryActionStatus.FirstComeFirstServeWaitlist]: {
      text: "You'll be notified of any open spots",
    },
    [EntryActionStatus.OpenWaitlist]: { text: 'Open spots available' },
  };

  const trainerActionTextProps: ActionTextProps = {
    text: 'Canceled',
    color: 'error.darkest',
    bgColor: 'error.lightest',
  };

  const trainerButtonProps: ButtonPropsWithLabel[] = [
    {
      label: 'Uncancel',
      color: 'neutral',
      variant: 'bordered',
      onClick: () => {
        uncancelSessionMutation.mutate(appointment);
      },
    },
  ];

  const checkInMutation = useMutation({
    mutationFn: (visit: Visit) => {
      return updateVisit({ status: VisitStatus.Completed, id: visit.id });
    },
    onSuccess: () => {
      // overkill, i would like to just replace the single visit in the appointment
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      toast.success('Check in complete');
    },
    onError: () => {
      toast.error('Check in could not be completed');
    },
    onSettled: () => {
      setShowConfirm(false);
    },
  });

  const registerMutation = useMutation({
    mutationFn: (visit: Visit) => {
      return updateVisit({ status: VisitStatus.Registered, id: visit.id });
    },
    onSuccess: () => {
      // overkill, i would like to just replace the single visit in the appointment
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      toast.success('Session confirmed');
    },
    onError: () => {
      toast.error('Session could not be confirmed');
    },
    onSettled: () => {
      setShowConfirm(false);
    },
  });

  const uncancelSessionMutation = useMutation({
    mutationFn: (appointment: Appointment) => {
      return updateAppointment({
        status: AppointmentStatus.Scheduled,
        id: appointment.id,
      });
    },
    onSuccess: () => {
      // overkill, i would like to just replace the single visit in the appointment
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      toast.success('Session uncanceled');
    },
    onError: () => {
      toast.error('Session could not be uncanceled');
    },
    onSettled: () => {
      setShowCancelOptions(false);
    },
  });

  const cancelMutation = useMutation({
    mutationFn: (visit: Visit) => {
      return updateVisit({
        status: VisitStatus.CancelOrLateCancelled, // server decides which
        id: visit.id,
      });
    },
    onSuccess: () => {
      // overkill, i would like to just replace the single visit in the appointment
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      toast.success('Session canceled');
    },
    onError: () => {
      toast.error('Session could not be canceled');
    },
    onSettled: () => {
      setShowCancelOptions(false);
    },
  });

  const deleteWaitListMutation = useMutation({
    mutationFn: (entry: WaitListEntry) => {
      return deleteWaitListEntry({ id: entry.id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      const selectedPersonName = selectedPerson?.title || 'You';
      const message =
        selectedPersonName === 'You'
          ? 'You have been removed from the waitlist'
          : `${selectedPersonName} has been removed from the waitlist`;
      toast.success(message);
    },
    onError: () => {
      const selectedPersonName = selectedPerson?.title || 'You';
      const message =
        selectedPersonName === 'You'
          ? 'You could not be removed from the waitlist'
          : `${selectedPersonName} could not be removed from the waitlist`;
      toast.error(message);
    },
    onSettled: () => {
      setShowConfirm(false);
    },
  });

  const visitMutation = useMutation({
    mutationFn: () => {
      const visit = {
        status: VisitStatus.Reserved,
        waitlistEntryId: entry.id,
        userId: selectedUserId,
      };

      return createAppointmentVisit({ appointment, visit });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      const selectedPersonName = selectedPerson?.title || 'You';
      const message =
        selectedPersonName === 'You'
          ? 'You have successfully booked this visit!'
          : `${selectedPersonName} has been successfully booked for this visit!`;
      toast.success(message);
    },
    onError: () => {
      toast.error('Error booking visit');
    },
  });

  const handleConfirm = () => {
    if (entry) {
      deleteWaitListMutation.mutate(entry);
    } else if (visit) {
      cancelMutation.mutate(visit);
    }
  };

  const handlWaitlistToVisit = async () => {
    // For dependents, we'll use the current user for eligibility check
    // since they are the account holder making the booking
    const warning = await queryClient.fetchQuery({
      queryKey: [
        'appointment',
        appointment.id,
        'user',
        selectedUserId,
        'eligibility',
      ],
      queryFn: () =>
        checkAppointmentEligibility({
          appointment,
          user,
        }),
    });

    if (warning.eligible) {
      visitMutation.mutate();
    } else {
      toast.error(warning.reasonMessage);
    }
  };

  const showUnCancel =
    appointment.status == AppointmentStatus.Canceled ||
    appointment.status == AppointmentStatus.LateCanceled;
  let textProps = trainerActionTextProps;
  let buttonProps = trainerButtonProps;

  // Get the name for the dialog title
  const selectedPersonName = selectedPerson?.title || 'You';

  if (!showUnCancel && (visit || entry)) {
    const status = determineStatus({ appointment, visit, entry });
    const actionTextProps = getActionTextProps(selectedPersonName);
    const textPropChoices = !entry ? actionTextProps : waitlistActionTextProps;
    const buttonPropChoices = !entry ? actionButtonProps : waitlistButtonProps;
    textProps = textPropChoices[status];
    buttonProps = buttonPropChoices[status];
  }
  const dialogTitle =
    selectedPersonName === 'You'
      ? t('changeSession')
      : `Change session for ${selectedPersonName}`;

  return {
    visit,
    entry,
    textProps,
    buttonProps,
    showConfirm,
    showCancelOptions,
    handleConfirm,
    showUnCancel,
    setShowConfirm,
    setShowCancelOptions,
    handleCancelConfirm: () => {
      setShowConfirm(false);
    },
    // Person selection logic
    showPersonSelector: peopleInAppointment.length > 1,
    personOptions,
    selectedPerson,
    selectedUserId,
    setSelectedUserId,
    dialogTitle,
  };
};
